/**
 * React Query hooks for leased asset OCR batch confirmation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';

import { useOrganizationId } from '@/hooks/use-organization-id';

import { confirmGhgOcrBatch } from './ocr';
import type { GhgOcrBatchConfirmRequest } from '../types/ocr';

/**
 * Hook for confirming a batch of OCR-extracted leased asset rows.
 */
export function useConfirmLeasedAssetBatch(uploadId: string) {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GhgOcrBatchConfirmRequest) => {
      if (!organizationId) {
        throw new Error('Organization not available');
      }
      return confirmGhgOcrBatch(getToken, organizationId, uploadId, data);
    },
    onSuccess: (result) => {
      const msg =
        result.skippedCount > 0
          ? `Created ${String(result.createdCount)} entries (${String(result.skippedCount)} skipped)`
          : `Created ${String(result.createdCount)} entries from leased asset data`;
      toast.success(msg);
      void queryClient.invalidateQueries({ queryKey: ['activityData'] });
      void queryClient.invalidateQueries({ queryKey: ['scope3Summary'] });
    },
    onError: (error: Error) => {
      toast.error(`Batch confirmation failed: ${error.message}`);
    },
  });
}
